"use client";

import React from "react";
import { calendarTheme } from "./calendarTheme";
import { ChevronLeft, ChevronRight, Clock, Calendar as CalendarIcon, AlertCircle } from "lucide-react";

type CalendarSlot = {
  id: number;
  startAt: number;
  endAt: number;
  isBooked?: boolean;
  isPast?: boolean;
};

type DayData = {
  key: string;
  date: Date;
  shortLabel: string;
  dateLabel: string;
  slots: CalendarSlot[];
};

type WeeklyCalendarProps = {
  timeZone?: string;
  days: DayData[];
  weekLabel: string;
  dateRangeLabel: string;
  loading: boolean;
  hasLoaded: boolean;
  weekFade: boolean;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  prevDisabled: boolean;
  nextDisabled: boolean;
  selectedSlotId: number | null;
  onSelectSlot: (slot: CalendarSlot) => void;
  activeDayKey: string;
  onActiveDayChange: (key: string) => void;
};

const navBtn =
  "inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm text-foreground hover:bg-secondary active:scale-[0.98] transition-all shadow-xs disabled:opacity-30 disabled:cursor-not-allowed";

function slotClass(status: "available" | "booked" | "selected" | "past") {
  const base =
    "group w-full flex items-center justify-between rounded-xl border py-2 px-3 text-sm transition-all duration-200 tabular-nums gap-2 shadow-xs";
  
  if (status === "booked") {
    return `${base} bg-muted/30 border-border text-muted-foreground cursor-not-allowed grayscale`;
  }
  if (status === "selected") {
    return `${base} bg-accent border-accent text-accent-foreground shadow-md scale-[1.02]`;
  }
  if (status === "past") {
    return `${base} bg-muted/20 border-border text-muted-foreground/30 cursor-not-allowed`;
  }
  return `${base} bg-card border-border text-foreground hover:border-primary/50 hover:bg-secondary/50 hover:shadow-md cursor-pointer`;
}

const SlotButton = React.memo(function SlotButton({
  slot,
  isSelected,
  onSelect,
  timeZone,
}: {
  slot: CalendarSlot;
  isSelected: boolean;
  onSelect: (slot: CalendarSlot) => void;
  timeZone?: string;
}) {
  const isBooked = Boolean(slot.isBooked);
  const isPast = Boolean(slot.isPast);
  const status = isSelected ? "selected" : isBooked ? "booked" : isPast ? "past" : "available";
  const label = new Date(slot.startAt).toLocaleTimeString("hu-HU", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <button
      type="button"
      className={slotClass(status)}
      onClick={() => {
        if (isBooked || isPast) {
          return;
        }
        onSelect(slot);
      }}
      aria-pressed={isSelected}
      disabled={isBooked || isPast}
    >
      <span className="font-bold sm:font-semibold">{label}</span>
      {isBooked && (
        <span className="text-[10px] uppercase tracking-wider opacity-60">
          Foglalt
        </span>
      )}
      {!isBooked && isSelected && (
        <span className="text-[10px] uppercase tracking-wider font-bold">
          Kiválasztva
        </span>
      )}
      {!isBooked && !isSelected && !isPast && (
        <span className="text-[10px] uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
          Szabad
        </span>
      )}
    </button>
  );
});

const DayColumn = React.memo(function DayColumn({
  day,
  selectedSlotId,
  onSelectSlot,
  timeZone,
}: {
  day: DayData;
  selectedSlotId: number | null;
  onSelectSlot: (slot: CalendarSlot) => void;
  timeZone?: string;
}) {
  return (
    <div
      className={`min-h-[420px] ${calendarTheme.radius} ${calendarTheme.card} border ${calendarTheme.border} px-3 py-4 flex flex-col`}
    >
      <div className="text-center mb-4">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-accent mb-1">
          {day.shortLabel}
        </div>
        <div className={`text-sm font-semibold ${calendarTheme.ink}`}>{day.dateLabel}</div>
      </div>
      <div className="grow space-y-2">
        {day.slots.length === 0 && (
          <div className="mt-8 text-center px-2">
            <AlertCircle className="w-5 h-5 text-muted-foreground/30 mx-auto mb-2" />
            <div className="text-[11px] text-muted-foreground/50 italic">Nincs szabad időpont</div>
          </div>
        )}
        {day.slots.map((slot) => (
          <SlotButton
            key={slot.id}
            slot={slot}
            isSelected={selectedSlotId === slot.id}
            onSelect={onSelectSlot}
            timeZone={timeZone}
          />
        ))}
      </div>
    </div>
  );
});

const MobileDayPicker = React.memo(function MobileDayPicker({
  days,
  selectedSlotId,
  onSelectSlot,
  weekFade,
  loading,
  timeZone,
}: {
  days: DayData[];
  selectedSlotId: number | null;
  onSelectSlot: (slot: CalendarSlot) => void;
  weekFade: boolean;
  loading: boolean;
  timeZone?: string;
}) {
  const hasAnySlots = days.some(day => day.slots.length > 0);

  return (
    <div
      className={`md:hidden transition-opacity duration-300 ${
        weekFade ? "opacity-60" : "opacity-100"
      }`}
    >
      {!hasAnySlots && !loading ? (
        <div className="py-20 px-6 text-center bg-card border-y border-border">
          <AlertCircle className="w-12 h-12 text-muted-foreground/20 mx-auto mb-4" />
          <h3 className="text-lg font-display font-bold text-foreground mb-2">Nincsenek szabad időpontok</h3>
          <p className="text-sm text-muted-foreground">Sajnos ezen a héten már minden időpont betelt. Kérjük, nézzen szét a következő héten!</p>
        </div>
      ) : (
        <div className="divide-y divide-border/50">
          {days.map((day) => {
            if (day.slots.length === 0) return null;
            
            return (
              <div key={day.key} className="p-6 bg-card">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-border/50">
                  <CalendarIcon className="w-4 h-4 text-accent" />
                  <div className="font-bold text-sm uppercase tracking-wider">
                    {day.shortLabel}, {day.dateLabel}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {day.slots.map((slot) => (
                    <SlotButton
                      key={slot.id}
                      slot={slot}
                      isSelected={selectedSlotId === slot.id}
                      onSelect={onSelectSlot}
                      timeZone={timeZone}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

export default function WeeklyCalendar({
  timeZone,
  days,
  weekLabel,
  dateRangeLabel,
  loading,
  hasLoaded,
  weekFade,
  onPrevWeek,
  onNextWeek,
  prevDisabled,
  nextDisabled,
  selectedSlotId,
  onSelectSlot,
  activeDayKey,
  onActiveDayChange,
}: WeeklyCalendarProps) {
  return (
    <div className="w-full">
      <div className="mx-auto max-w-7xl">
        <div
          className={`overflow-hidden md:${calendarTheme.radius} ${calendarTheme.card} border-y md:border-x ${calendarTheme.border} md:${calendarTheme.shadow}`}
        >
          {/* Calendar Header */}
          <div className="flex items-center justify-between px-4 sm:px-8 py-4 sm:py-6 border-b border-border bg-secondary/30">
            <button
              type="button"
              className={navBtn}
              onClick={onPrevWeek}
              disabled={prevDisabled}
              aria-label="Előző hét"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="hidden sm:inline font-medium">Előző hét</span>
            </button>

            <div className="text-center">
              <div className={`text-xl sm:text-2xl font-display font-bold ${calendarTheme.ink}`}>
                {weekLabel}
              </div>
              <div className={`mt-1 text-xs sm:text-sm font-medium ${calendarTheme.muted} flex items-center justify-center gap-1.5`}>
                <Clock className="w-3.5 h-3.5" />
                {dateRangeLabel}
              </div>
            </div>

            <button
              type="button"
              className={navBtn}
              onClick={onNextWeek}
              disabled={nextDisabled}
              aria-label="Következő hét"
            >
              <span className="hidden sm:inline font-medium">Következő hét</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Calendar Body */}
          <div className="relative">
            {!hasLoaded && loading ? (
              <div className="py-24 flex flex-col items-center justify-center text-muted-foreground">
                <div className="w-10 h-10 border-4 border-accent border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="font-medium">Időpontok betöltése...</p>
              </div>
            ) : (
              <>
                <div
                  className={`hidden md:grid ${days.length > 5 ? "grid-cols-7" : "grid-cols-5"} gap-3 p-6 transition-all duration-300 ${
                    weekFade ? "opacity-40 scale-[0.99] grayscale-[0.2]" : "opacity-100 scale-100"
                  }`}
                >
                  {days.map((day) => (
                    <DayColumn
                      key={day.key}
                      day={day}
                      selectedSlotId={selectedSlotId}
                      onSelectSlot={onSelectSlot}
                      timeZone={timeZone}
                    />
                  ))}
                </div>
                <MobileDayPicker
                  days={days}
                  selectedSlotId={selectedSlotId}
                  onSelectSlot={onSelectSlot}
                  weekFade={weekFade}
                  loading={loading}
                  timeZone={timeZone}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
